import React, { useMemo, useCallback, useState } from 'react'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Badge from '@mui/material/Badge'
import HolidayIcon from '@mui/icons-material/CardGiftcard'
import HomeIcon from '@mui/icons-material/Home'
import BusinessIcon from '@mui/icons-material/Business'
import WorkIcon from '@mui/icons-material/Work'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import PaidIcon from '@mui/icons-material/Paid'
import ClockIcon from '@mui/icons-material/Watch'
import QuitIcon from '@mui/icons-material/ExitToApp'
import PermissionIcon from '@mui/icons-material/Security'
import CityIcon from '@mui/icons-material/LocationCity'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EventNoteIcon from '@mui/icons-material/EventNote'
import DescriptionIcon from '@mui/icons-material/Description'
import ListAltIcon from '@mui/icons-material/ListAlt'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen'
import SavingsIcon from '@mui/icons-material/Savings'
import GavelIcon from '@mui/icons-material/Gavel'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import { useNavigate, useParams } from 'react-router-dom'
import useInstallPrompt from '../../hooks/useInstallPrompt'

import {auth} from '../../services/firebase/app'
import usePendingApprovalsCount from '../../hooks/usePendingApprovalsCount'
import useUser from '../../services/useUser'
import { clearInstallDismissal } from '../../services/pwaInstall'
import InstallInstructionsDialog from '../InstallPwaDialog/InstallInstructionsDialog'
import { revokeThisDevice } from '../../services/platform/nativeDeviceRegistry'

const SideMenu = ({ onClose }) => {
  const { isInstalled, isSupported, promptInstall } = useInstallPrompt()
  const [instructionsOpen, setInstructionsOpen] = useState(false)
  // #330: the entry is offered whether or not the browser will let us ask.
  // Gating it on `isSupported` hid it on every iPhone — where the event does
  // not exist at all — and left "do not show again" as a one-way door.
  const handleInstall = useCallback(async () => {
    clearInstallDismissal()
    if (isSupported) {
      onClose()
      await promptInstall()
      return
    }
    setInstructionsOpen(true)
  }, [onClose, promptInstall, isSupported])
  const handleInstructionsClose = useCallback(() => {
    setInstructionsOpen(false)
    onClose()
  }, [onClose])

  const user = useUser()
  const claims = user?.customClaims
  const isAdmin = claims?.role === 'admin'
  const isEmployee = claims?.role === 'employee'
  const isPayrollAccountant = claims?.role === 'payrollAccountant'
  const isEmployerAdmin = claims?.role === 'employerAdmin' || (!isAdmin && !isEmployee && !isPayrollAccountant && Boolean(claims?.employer))
  const employerId = claims?.employer
  const employeeId = claims?.employee
  // A payroll accountant carries `employers[]`, never the scalar `employer`,
  // so their active employer only exists in the route. Without this the
  // dashboard — the ONLY place the payroll-export button lives — was
  // unreachable for the very role whose job is producing that file: the
  // employers list links to /calculations, and this menu item was gated on a
  // claim they do not have. Read from the route rather than useEmployerId(),
  // which throws for a user with no employer at all and would take the whole
  // header down with it.
  const { employerId: routeEmployerId } = useParams()
  const dashboardEmployerId = isPayrollAccountant ? routeEmployerId : employerId
  const pendingApprovalsCount = usePendingApprovalsCount(isEmployerAdmin ? employerId : null)
  
  const handleSignOut = useCallback(async () => {
    // #1691, spec.md §5.2: logout must revoke the NATIVE_DEVICE server-side
    // — no-op on web (revokeThisDevice() checks isNativePlatform() itself).
    // Called BEFORE auth.signOut(): the callable needs the still-valid
    // Firebase Auth token, and a device-revoke failure must never block the
    // user from actually signing out (hence the try/catch swallow, not a
    // rethrow) — a stuck revoke is a device left ACTIVE, which the employee
    // or their employer can still revoke later; a stuck sign-out would be a
    // user unable to leave the app.
    try {
      await revokeThisDevice('user_logout')
    } catch {
      // Intentionally swallowed — see comment above.
    }
    auth.signOut()
  }, [])

  const navigate = useNavigate()
  const LinkListItem = useMemo(() => ({ to, icon, children, onClick, ...other }) => (
    <ListItem
      button
      onClick={() => {
        // Run any item-specific handler (e.g. sign-out) FIRST, then always
        // navigate and close the drawer — otherwise a passed onClick would
        // override the built-in behaviour (leaving stale breadcrumbs and an
        // open drawer on logout).
        onClick?.()
        navigate(to)
        onClose()
      }}
      {...other}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={children} sx={{ textAlign: 'right' }}/>
    </ListItem>
  ), [navigate, onClose])

  return (
    <List>
      {/* Admin + payroll accountant: Employers list (filtered for accountant) */}
      {(isAdmin || isPayrollAccountant) && (
        <LinkListItem
          icon={<BusinessIcon/>}
          to="/employers"
        >
          מעסיקים
        </LinkListItem>
      )}
      
      {/* The dashboard home (#177) — employer admin, and the payroll
          accountant once an employer is selected (#228 pilot). */}
      {(isEmployerAdmin || isPayrollAccountant) && dashboardEmployerId && (
        <LinkListItem
          data-testid="side-menu-dashboard-link"
          icon={<HomeIcon/>}
          to={`/employers/${dashboardEmployerId}/dashboard`}
        >
          דף הבית
        </LinkListItem>
      )}

      {/* EmployerAdmin only: the reports journal (#177 T10) */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-reports-link"
          icon={<ListAltIcon/>}
          to={`/employers/${employerId}/reports`}
        >
          דיווחים
        </LinkListItem>
      )}

      {/* EmployerAdmin only: the attendance board (#177 T11) */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-attendance-board-link"
          icon={<CalendarMonthIcon/>}
          to={`/employers/${employerId}/attendance-board`}
        >
          לוח נוכחות
        </LinkListItem>
      )}

      {/* EmployerAdmin only: approved absences (#177) */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-absences-link"
          icon={<EventBusyIcon/>}
          to={`/employers/${employerId}/absences`}
        >
          היעדרויות מאושרות
        </LinkListItem>
      )}

      {/* EmployerAdmin only: central reports hub (#177 T12) */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-reports-hub-link"
          icon={<DescriptionIcon/>}
          to={`/employers/${employerId}/reports-hub`}
        >
          דוחות
        </LinkListItem>
      )}

      {/* EmployerAdmin only: Calculations for their employer */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          icon={<BusinessIcon/>}
          to={`/employers/${employerId}`}
        >
          הגדרות מעסיק
        </LinkListItem>
      )}

      {isEmployerAdmin && employerId && (
        <LinkListItem
          icon={<WorkIcon/>}
          to={`/employers/${employerId}/calculations`}
        >
          עובדים
        </LinkListItem>
      )}

      {/* #599: moved out of the calculations screen's TopActionBar — same
          permission and the same #1-employer exception it had there. */}
      {isEmployerAdmin && employerId && Number(employerId) !== 1 && (
        <LinkListItem
          data-testid="side-menu-agreements-link"
          icon={<GavelIcon/>}
          to={`/employers/${employerId}/agreements`}
        >
          הסכמים
        </LinkListItem>
      )}

      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-anomalies-link"
          icon={(
            <Badge badgeContent={pendingApprovalsCount} color="error" invisible={pendingApprovalsCount <= 0} max={99}>
              <WarningAmberIcon/>
            </Badge>
          )}
          to={`/employers/${employerId}/anomalies`}
        >
          חריגות ובקשות
        </LinkListItem>
      )}

      {/* #237 task 13.3 / spec 18.2 — the replacement screen. Placed beside the
          anomalies screen because it answers the same kind of question: what
          does this month contain that somebody has to look at before payroll
          runs. */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-wage-replacements-link"
          icon={<PaidIcon/>}
          to={`/employers/${employerId}/wage-replacements`}
        >
          חלפי שכר
        </LinkListItem>
      )}

      {/* #766 / spec 288 §18.1 item 9 — a central, independent place to enter
          and approve variable pay components (bonus, commission…), separate
          from the raw hours report and from month-closing. Same placement
          logic as the wage-replacements screen above. */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-variable-components-link"
          icon={<RequestQuoteIcon/>}
          to={`/employers/${employerId}/variable-components`}
        >
          רכיבי שכר משתנים
        </LinkListItem>
      )}

      {/* #730 — #288 spec.md §35, "שכר וייצוא": the payroll drafts list. */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-payroll-previews-link"
          icon={<ReceiptLongIcon/>}
          to={`/employers/${employerId}/payroll-previews`}
        >
          טיוטות שכר
        </LinkListItem>
      )}

      {/* #886 — #248 spec.md §10, the month-close screen ("סגירת חודש").
          Same lesson #237 stage 0.3 already documents below (a route with no
          way to reach it): this link exists so `/month-close/:year/:month`
          is not that a second time. Points at the CURRENT calendar month —
          same "today, not a saved preference" convention `PayrollPreviewList`
          already uses for its own default filters. */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-month-close-link"
          icon={<FactCheckIcon/>}
          to={`/employers/${employerId}/month-close/${new Date().getFullYear()}/${new Date().getMonth() + 1}`}
        >
          סגירת חודש
        </LinkListItem>
      )}

      {/* #985 — #892 spec.md §23ב, the employer's "בקשות ואירועים
          רטרואקטיביים לטיפול" queue. Same lesson as the month-close link
          above: a route with no way to reach it is not reachable. */}
      {(isEmployerAdmin || isPayrollAccountant) && employerId && (
        <LinkListItem
          data-testid="side-menu-retroactive-items-link"
          icon={<PendingActionsIcon/>}
          to={`/employers/${employerId}/retroactive-items`}
        >
          פניות רטרואקטיביות
        </LinkListItem>
      )}

      {/* #237 stage 0.3 — the opening-balances completion screen. It had a
          route and no way to reach it, which for a screen whose whole job is
          "here is what still has to be filled in before the engine can run"
          means nobody would ever see the list. */}
      {isEmployerAdmin && employerId && (
        <LinkListItem
          data-testid="side-menu-opening-balances-link"
          icon={<PlaylistAddCheckIcon/>}
          to={`/employers/${employerId}/opening-balances`}
        >
          יתרות פתיחה
        </LinkListItem>
      )}
      
      {/* Employee only: Attendance reporting */}
      {isEmployee && (
        <LinkListItem
          data-testid="side-menu-attendance-link"
          icon={<AccessTimeIcon/>}
          to="/attendance"
        >
          דיווח נוכחות
        </LinkListItem>
      )}

      {/* Employee only: #821 — vacation/sick/reserve-duty requests hub. */}
      {isEmployee && (
        <LinkListItem
          data-testid="side-menu-absence-requests-link"
          icon={<EventNoteIcon/>}
          to="/attendance/requests"
        >
          בקשות היעדרות
        </LinkListItem>
      )}

      {/* Employee only: My Timesheets */}
      {isEmployee && employerId && employeeId && (
        <LinkListItem
          icon={<DescriptionIcon/>}
          to={`/employers/${employerId}/calculations/${employeeId}/timesheets`}
        >
          דוחות שעות
        </LinkListItem>
      )}

      <LinkListItem
        data-testid="side-menu-notifications-link"
        icon={<NotificationsNoneIcon/>}
        to="/notifications"
      >
        הודעות
      </LinkListItem>
      
      {/* Admin only: Holidays */}
      {isAdmin && (
        <LinkListItem
          icon={<HolidayIcon/>}
          to="/holidays"
        >
          חגים
        </LinkListItem>
      )}
      
      {/* Admin only: Daylight Saving */}
      {isAdmin && (
        <LinkListItem
          icon={<ClockIcon/>}
          to="/daylight-saving"
        >
          שעון קיץ/חורף
        </LinkListItem>
      )}
      
      {/* Admin only: Cities */}
      {isAdmin && (
        <LinkListItem
          icon={<CityIcon/>}
          to="/cities"
        >
          ערים
        </LinkListItem>
      )}
      
      {/* Admin only: the retroactive accrual fill (#390 item 4). Admin-only on
          the server too — this entry is the screen it never had. */}
      {isAdmin && (
        <LinkListItem
          icon={<SavingsIcon/>}
          to="/accrual-backfill"
        >
          הרצת צבירות
        </LinkListItem>
      )}
      
      {/* Admin only: Permissions */}
      {isAdmin && (
        <LinkListItem
          icon={<PermissionIcon/>}
          to="/permissions"
        >
          הרשאות
        </LinkListItem>
      )}
      
      {/* #330: offered whenever the app is not installed. Where the browser
          cannot be asked programmatically, the entry explains the manual route
          instead of disappearing. */}
      {!isInstalled && (
        <ListItem button onClick={handleInstall}>
          <ListItemIcon><AddToHomeScreenIcon/></ListItemIcon>
          <ListItemText primary="הוסף למסך הבית" sx={{ textAlign: 'right' }}/>
        </ListItem>
      )}
      <InstallInstructionsDialog
        open={instructionsOpen}
        onClose={handleInstructionsClose}
      />

      {/* Everyone: Logout */}
      <LinkListItem
        icon={<QuitIcon/>}
        to="/"
        onClick={handleSignOut}
      >
          יציאה
      </LinkListItem>
    </List>
  )
}

export default SideMenu
